import { useCallback, useRef, useState } from 'react'
import type { Coordinate, DragModeName, DragRect } from '../types'

export interface DragSession {
  mode: DragModeName
  target: 'map' | 'bank' | 'side' | null
  startCell: Coordinate | null
  endCell: Coordinate | null
  rect: DragRect | null
  active: boolean
}

const initialSession: DragSession = {
  mode: 'tie',
  target: null,
  startCell: null,
  endCell: null,
  rect: null,
  active: false,
}

export function useDragMode() {
  const currentMode: DragModeName = 'tie' // 항상 묶기 모드
  const [session, setSession] = useState<DragSession>(initialSession)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /** 드래그 시작 */
  const startDrag = useCallback(
    (cell: Coordinate, target: 'map' | 'bank' | 'side') => {
      setSession({
        mode: 'tie',
        target,
        startCell: cell,
        endCell: cell,
        rect: { startCol: cell.col, startRow: cell.row, endCol: cell.col, endRow: cell.row },
        active: true,
      })
    },
    [],
  )

  /** 드래그 중 업데이트 */
  const updateDrag = useCallback((cell: Coordinate) => {
    setSession(prev => {
      if (!prev.active || !prev.startCell) return prev
      const startCol = Math.min(prev.startCell.col, cell.col)
      const startRow = Math.min(prev.startCell.row, cell.row)
      const endCol = Math.max(prev.startCell.col, cell.col)
      const endRow = Math.max(prev.startCell.row, cell.row)
      return {
        ...prev,
        endCell: cell,
        rect: { startCol, startRow, endCol, endRow },
      }
    })
  }, [])

  /** 드래그 종료 - 정규화된 rect 반환 */
  const endDrag = useCallback((): DragSession => {
    const current = { ...session }
    setSession(prev => ({ ...prev, active: false, rect: null, startCell: null, endCell: null }))
    return current
  }, [session])

  /** 드래그 취소 */
  const cancelDrag = useCallback(() => {
    setSession(prev => ({ ...prev, active: false, rect: null, startCell: null, endCell: null }))
  }, [])

  /** 롱프레스 시작 */
  const startLongPress = useCallback((callback: () => void, duration = 500) => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
    longPressTimer.current = setTimeout(callback, duration)
  }, [])

  /** 롱프레스 취소 */
  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  return {
    currentMode,
    session,
    startDrag,
    updateDrag,
    endDrag,
    cancelDrag,
    startLongPress,
    cancelLongPress,
  }
}
